import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { UserProfile } from '@/lib/types'
import { cn } from '@/lib/utils'
import { Building2, UserCircle, Loader2, ArrowRight, ArrowLeft, LogOut } from 'lucide-react'

interface Props {
  /** The (new) invitee's profile — may already carry inviter-provided prefill. */
  profile: UserProfile
  /** Persist the confirmed company + mandatory name and flip onboarded. */
  onComplete: (patch: {
    first_name: string
    last_name: string
    company_legal_name: string | null
  }) => Promise<void>
  onSignOut: () => void
}

/** First-time onboarding for invited members: confirm the company (prefilled
 *  by the inviter, optional) then set a MANDATORY first/last name before the
 *  board unlocks. */
export function Onboarding({ profile, onComplete, onSignOut }: Props) {
  const { t } = useTranslation()
  const [step, setStep] = useState<'company' | 'name'>('company')
  const [company, setCompany] = useState(profile.company_legal_name ?? '')
  const [firstName, setFirstName] = useState(profile.first_name ?? '')
  const [lastName, setLastName] = useState(profile.last_name ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const nameValid = firstName.trim().length > 0 && lastName.trim().length > 0

  async function finish() {
    if (!nameValid || busy) return
    setBusy(true)
    setError(null)
    try {
      await onComplete({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        company_legal_name: company.trim() || null,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="mb-5 flex flex-col items-center gap-2 text-center">
          <img
            src="/favicon.svg"
            alt="msgban"
            className="h-12 w-12 rounded-2xl shadow-[var(--shadow-soft)]"
          />
          <div className="text-[13px] text-[var(--color-text-dim)]">
            {t('onboarding.welcome')}
          </div>
        </div>

        <div className="rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-6 shadow-[var(--shadow-modal)]">
          {/* Step indicator */}
          <div className="mb-4 flex items-center justify-center gap-1.5">
            {(['company', 'name'] as const).map((s) => (
              <span
                key={s}
                className={cn(
                  'h-1.5 w-8 rounded-full transition-colors',
                  step === s ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-surface-3)]',
                )}
              />
            ))}
          </div>

          {step === 'company' ? (
            <>
              <div className="mb-1 flex items-center gap-1.5 text-[15px] font-semibold text-[var(--color-text-strong)]">
                <Building2 className="h-4 w-4 text-[var(--color-text-dim)]" />
                {t('onboarding.companyTitle')}
              </div>
              <p className="mb-4 text-[12px] text-[var(--color-text-dim)]">
                {t('onboarding.companyHint')}
              </p>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder={t('onboarding.companyPlaceholder')}
                className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
              />
              <button
                type="button"
                onClick={() => setStep('name')}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--color-accent)] px-4 py-2.5 text-[13px] font-semibold text-white shadow-[var(--shadow-soft)] transition-all hover:bg-[var(--color-accent-hover)]"
              >
                {t('common.next')}
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <div className="mb-1 flex items-center gap-1.5 text-[15px] font-semibold text-[var(--color-text-strong)]">
                <UserCircle className="h-4 w-4 text-[var(--color-text-dim)]" />
                {t('onboarding.nameTitle')}
              </div>
              <p className="mb-4 text-[12px] text-[var(--color-text-dim)]">
                {t('onboarding.nameHint')}
              </p>
              <div className="flex flex-col gap-2">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('onboarding.firstNamePlaceholder')}
                  autoFocus
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
                />
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('onboarding.lastNamePlaceholder')}
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
                />
              </div>
              {error && (
                <div className="mt-3 rounded-xl border border-red-200 bg-[var(--color-danger-bg)] px-3 py-2 text-[11.5px] text-[var(--color-danger)]">
                  {error}
                </div>
              )}
              <div className="mt-5 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setStep('company')}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[var(--color-border)] px-3 py-2.5 text-[13px] font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('common.back')}
                </button>
                <button
                  type="button"
                  onClick={finish}
                  disabled={!nameValid || busy}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[var(--color-accent)] px-4 py-2.5 text-[13px] font-semibold text-white shadow-[var(--shadow-soft)] transition-all hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t('onboarding.finish')}
                </button>
              </div>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={onSignOut}
          className="mt-4 inline-flex w-full items-center justify-center gap-1.5 text-[12px] text-[var(--color-text-dim)] transition-colors hover:text-[var(--color-text)]"
        >
          <LogOut className="h-3.5 w-3.5" />
          {t('topbar.signOut')}
        </button>
      </div>
    </div>
  )
}
